import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAppVersion } from '../utils/api';

function HelpFeedback({ userInfo }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const [changelogOpen, setChangelogOpen] = useState(false);

  const username = userInfo?.username || 'Unknown';

  useEffect(() => {
    getAppVersion()
      .then((res) => setVersionInfo(res.data))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSending(true);
    setSuccess(null);
    setError(null);

    try {
      const response = await axios.post('/api/feedback', { message: message.trim() }, { withCredentials: true });
      setSuccess(
        response.data.emailSent
          ? 'Thank you! Your feedback has been sent to our support team.'
          : 'Thank you! Your feedback has been recorded. (Email delivery is currently unavailable, but our team will review it.)'
      );
      setMessage('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Help & Feedback</h1>

      {/* About Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">About</h2>
        <div className="space-y-4">
          <p className="text-gray-700 text-lg">
            <span className="font-semibold">TCS Insight</span> is a product of{' '}
            <span className="font-semibold">MPAMOT Digital Solutions</span>.
          </p>

          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">PLATFORM USAGE TERMS & LIMITATIONS</h3>
            <div className="text-gray-700 leading-relaxed space-y-3 text-sm">
              <p>
                All data on this platform is the property of its respective owners and is securely stored and managed in accordance with industry standards. The data is used as provided and is not altered in any way. MPAMOT, as the platform provider, cannot be held responsible for any inaccuracies or discrepancies in the data presented.
              </p>
              <p>
                Unauthorized access, sharing, or export of data is strictly prohibited, and data export is only permitted with explicit owner consent.
              </p>
              <p>
                By using this platform, you acknowledge these terms and understand that misuse may result in access restrictions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Send Feedback</h2>
        <p className="text-sm text-gray-600 mb-4">
          Have a question, suggestion, or issue? Let us know and our team will get back to you.
        </p>

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{success}</span>
            <button onClick={() => setSuccess(null)} className="absolute top-0 bottom-0 right-0 px-4 py-3">
              <span className="text-xl">&times;</span>
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button onClick={() => setError(null)} className="absolute top-0 bottom-0 right-0 px-4 py-3">
              <span className="text-xl">&times;</span>
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Username</label>
            <input
              type="text"
              value={username}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-600 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              required
              placeholder="Describe your feedback, question, or issue..."
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-vertical"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={sending || !message.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded text-sm font-medium disabled:opacity-50 flex items-center"
            >
              {sending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Feedback
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Version Info */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">TCS Insight</h3>
            <p className="text-xs text-gray-500">
              Version {versionInfo?.current?.version || '1.0.0'}
            </p>
            {versionInfo?.current?.notes && (
              <p className="text-xs text-gray-400 mt-1">{versionInfo.current.notes}</p>
            )}
          </div>
          <p className="text-xs text-gray-400">MPAMOT Digital Solutions</p>
        </div>

        {/* Changelog */}
        {versionInfo?.changelog?.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <button
              onClick={() => setChangelogOpen(!changelogOpen)}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              <svg
                className={`w-4 h-4 mr-1 transition-transform ${changelogOpen ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Changelog
            </button>
            {changelogOpen && (
              <div className="mt-3 space-y-2">
                {versionInfo.changelog.map((entry) => (
                  <div key={entry.id} className="flex items-start text-xs">
                    <span className="font-semibold text-gray-700 w-16 flex-shrink-0">{entry.version}</span>
                    <span className="text-gray-400 w-24 flex-shrink-0">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </span>
                    <span className="text-gray-600">{entry.notes || '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HelpFeedback;
